import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchUnratedMissions } from "../../services/ratingService";
import { usePagePermission } from "../../hooks/usePagePermission";
import { PageName, PermissionAction } from "../../types/Authorization";
import classes from "./rating.module.scss";

/**
 * Compact pill in the header showing how many of the current user's completed missions
 * still need rating. Hidden when count is 0 or when the user lacks read permission on
 * organization ratings (the call is gated to avoid global 403 → /denied redirects on
 * every page render for users who don't have rating access).
 */
const UnratedMissionsBadge: React.FC = () => {
    const [count, setCount] = useState<number>(0);
    const [missionIds, setMissionIds] = useState<number[]>([]);

    const canRead = usePagePermission(PageName.ORGANIZATION_RATINGS, PermissionAction.READ);

    const refresh = async () => {
        if (!canRead) return;
        try {
            const ids = await fetchUnratedMissions();
            if (Array.isArray(ids)) {
                setMissionIds(ids);
                setCount(ids.length);
            }
        } catch {
            // silently ignore — non-critical sidebar widget, must never block the app
        }
    };

    useEffect(() => {
        if (!canRead) return;
        refresh();
        const interval = setInterval(refresh, 60_000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canRead]);

    if (!canRead || count === 0) {
        return null;
    }

    const firstId = missionIds[0];
    return (
        <Link
            to={firstId ? `/mission/${firstId}` : "/missions"}
            className={classes.unratedPill}
            title={`${count} completed mission${count === 1 ? "" : "s"} awaiting rating`}
        >
            <i className="bi bi-star-fill" />
            {count} unrated mission{count === 1 ? "" : "s"}
        </Link>
    );
};

export default UnratedMissionsBadge;
